/**
 * search-lowest-variant.js
 *
 * Corrects each search result PRICE and link to the TRUE lowest-priced variant
 * that is actually in stock.
 *
 * Why: the Liquid in search-ajax.liquid can only see 250 variants of a single
 * product and cannot see sibling/sub-products, so for large or split products the
 * link can point at a higher-priced colour than the "as low as" price shown.
 *
 * This uses the Storefront API (paginates past 250, and can look across the whole
 * style family) to find the real lowest-priced variant, then rewrites the link.
 * On any error it silently leaves the Liquid link as-is.
 *
 * Requires window.CCSearchCfg = { token, domain } (set in search-ajax.liquid).
 */
(function () {
  var CFG = window.CCSearchCfg || {};
  var TOKEN = CFG.token || '';
  var DOMAIN = CFG.domain || '';
  if (!TOKEN || !DOMAIN) return; // token not exposed; leave links untouched
  document.documentElement.classList.add('cc-search-fixing'); // blurs prices until each is final (CSS in loader)

  var API = 'https://' + DOMAIN + '/api/2024-01/graphql.json';

  // Stock means the same thing as on the product page: availableForSale is true for
  // untracked / "continue selling" variants, so a variant with 0 in every warehouse
  // was being picked as the lowest price. Check the warehouse metafields too.
  var WH_KEYS = ['fresno_ca','reno_nv','fort_worth_tx','olathe_ks','west_chester_township_oh',
                 'mcdonough_ga','lockport_il','orlando_fl','middleborough_ma','reading_pa'];
  var WH_FIELDS = 'metafields(identifiers:[' + WH_KEYS.map(function (k) {
    return '{namespace:"custom",key:"' + k + '"}';
  }).join(',') + ']){key value}';

  function inStock(node) {
    if (!node.availableForSale) return false;
    var mfs = (node.metafields || []).filter(Boolean);
    if (!mfs.length) return true; // no warehouse data: trust availableForSale
    return mfs.some(function (m) { return parseInt(m.value, 10) > 0; });
  }

  // Format with the shop's own money format so the corrected price matches the theme.
  // The API gives the amount as a decimal STRING ("3.11"). Multiplying a float by
  // 100 can land just under a half cent ("1.005" * 100 = 100.49999...) and round the
  // wrong way, so read the digits instead. Liquid never sees this: it gets whole cents.
  function toCents(amount) {
    var str = String(amount).trim();
    var neg = str.charAt(0) === '-';
    if (neg) str = str.slice(1);
    var parts = str.split('.');
    var whole = parseInt(parts[0], 10) || 0;
    var frac = (parts[1] || '') + '00';
    var cents = whole * 100 + parseInt(frac.slice(0, 2), 10);
    if (parseInt(frac.charAt(2), 10) >= 5) { cents += 1; } // third decimal rounds up
    return neg ? -cents : cents;
  }

  function formatMoney(amount) {
    var cents = toCents(amount);
    var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    function grouped(decimals, dot, comma) {
      var parts = (cents / 100).toFixed(decimals).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + comma);
      return parts[1] ? whole + dot + parts[1] : whole;
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount_no_decimals': return grouped(0, '.', ',');
        case 'amount_with_comma_separator': return grouped(2, ',', '.');
        case 'amount_no_decimals_with_comma_separator': return grouped(0, ',', '.');
        default: return grouped(2, '.', ',');
      }
    });
  }

  var processed = new WeakSet();
  var cacheByProduct = {}; // productId -> {id, price}

  function gql(query, variables) {
    return fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function (r) { return r.json(); });
  }

  // Lowest-priced AVAILABLE variant of one product (paginated). Falls back to the
  // lowest-priced variant overall if none are available, so the link is never broken.
  var PREFER_IN_STOCK = false;

  function lowestForProduct(productId) {
    if (cacheByProduct[productId]) return Promise.resolve(cacheByProduct[productId]);
    var gid = 'gid://shopify/Product/' + productId;
    var lowAvail = { id: null, price: Infinity };  // lowest price among in-stock
    var lowAny   = { id: null, price: Infinity };   // lowest price overall

    function page(cursor) {
      var q = 'query($id:ID!,$cursor:String){product(id:$id){variants(first:250,after:$cursor){pageInfo{hasNextPage endCursor}edges{node{id price{amount} availableForSale ' + WH_FIELDS + '}}}}}';
      return gql(q, { id: gid, cursor: cursor || null }).then(function (d) {
        var p = d && d.data && d.data.product;
        if (!p) return finalize();
        p.variants.edges.forEach(function (e) {
          var pr = parseFloat(e.node.price.amount);
          if (isNaN(pr)) return;
          var vid = e.node.id.replace('gid://shopify/ProductVariant/', '');
          if (pr < lowAny.price) { lowAny = { id: vid, price: pr }; }
          if (inStock(e.node) && pr < lowAvail.price) {
            lowAvail = { id: vid, price: pr };
          }
        });
        if (p.variants.pageInfo.hasNextPage) return page(p.variants.pageInfo.endCursor);
        return finalize();
      });
    }
    function finalize() {
      // Match the collection cards: use Shopify's lowest price (even if out of stock) and
      // open that exact variant (Nick, Oct 2026). Set PREFER_IN_STOCK = true to go back
      // to the lowest IN-STOCK variant.
      var res = PREFER_IN_STOCK ? (lowAvail.id ? lowAvail : lowAny) : (lowAny.id ? lowAny : lowAvail);
      if (res.id) cacheByProduct[productId] = res;
      return res;
    }
    return page(null);
  }

  function currentVariant(href) {
    var m = href.match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }

  function fix(link) {
    if (processed.has(link)) return;
    processed.add(link);

    var pid = link.getAttribute('data-product-id');
    var card = link.closest('.product-item'); // collection/product cards too, not just search
    var ready = function () { link.classList.add('price-ready'); if (card) card.classList.add('price-ready'); };
    if (!pid) return ready();
    setTimeout(ready, 4000); // never keep a price blurred for long

    // Find the lowest-priced variant WITHIN THIS PRODUCT ONLY (paginated past 250).
    // NOTE: we deliberately do NOT group by styleid — on this store styleid is not a
    // unique identifier (e.g. "16" is shared across 50 unrelated products), so grouping
    // by it links to wrong products. The correct lowest price is within the same product.
    link._fixP = lowestForProduct(pid).then(function (best) {
      link._fixDone = true;
      if (!best || !best.id) return ready();
      var href = link.getAttribute('href');
      var path = href.split('?')[0];

      // Preserve existing query params (_pos/_sid/_ss) and set the correct variant,
      // as a proper query param so we never produce a malformed URL with two "?".
      var params = new URLSearchParams();
      var qIndex = href.indexOf('?');
      if (qIndex !== -1) {
        var existing = href.substring(qIndex + 1).replace(/\?/g, '&');
        params = new URLSearchParams(existing);
      }
      params.set('variant', best.id);

      var newHref = path + '?' + params.toString();
      if (href !== newHref) {
        link.setAttribute('href', newHref);
      }
      // On cards, point the image and "View Product" links at the same variant.
      if (card) {
        card.querySelectorAll('a[href]').forEach(function (a) {
          if (a !== link && a.getAttribute('href').split('?')[0] === path) a.setAttribute('href', newHref);
        });
      }

      // The Liquid price is item.price (product.price_min), which counts sold-out
      // variants, so it can advertise a price nobody can buy. Show the price of the
      // variant we are actually linking to.
      var priceEl = link.querySelector('.search-bar__item-price') || (card && card.querySelector('.js-low-price'));
      if (priceEl && typeof best.price === 'number') {
        var newText = formatMoney(best.price);
        if (priceEl.textContent.trim() !== newText) { priceEl.textContent = newText; }
      }
      ready();
    }).catch(function () { link._fixDone = true; ready(); /* leave Liquid link as-is */ });
  }

  // The theme may re-append tracking params (_pos/_sid/_ss) to result links after
  // we set them, sometimes producing a stray second "?". Repair any such links.
  function repairDoubleQuestion() {
    var links = document.querySelectorAll('.js-search-lowest[href]');
    for (var i = 0; i < links.length; i++) {
      var h = links[i].getAttribute('href');
      var first = h.indexOf('?');
      if (first !== -1 && h.indexOf('?', first + 1) !== -1) {
        // replace every ? after the first with &
        var fixed = h.substring(0, first + 1) + h.substring(first + 1).replace(/\?/g, '&');
        links[i].setAttribute('href', fixed);
      }
    }
  }

  function fixAll() {
    var links = document.querySelectorAll('.js-search-lowest[data-product-id]');
    for (var i = 0; i < links.length; i++) fix(links[i]);
    repairDoubleQuestion();
  }

  // Search results are injected via AJAX; watch for them and fix on appearance.
  if (window.MutationObserver) {
    var t = null;
    var mo = new MutationObserver(function () {
      if (t) return;
      t = setTimeout(function () { t = null; fixAll(); }, 200);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixAll);
  } else {
    fixAll();
  }

  // Final safety net: repair a malformed href at the moment of click, so even if
  // the theme re-appends params after our fix, the navigated URL is valid.
  document.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('.js-search-lowest[href]') : null;
    if (!a) return;
    // Clicked before the lowest-variant lookup finished: wait for it (max 2.5s), then go.
    if (a._fixP && !a._fixDone && !(e.metaKey || e.ctrlKey || e.shiftKey || e.button)) {
      e.preventDefault();
      e.stopPropagation();
      Promise.race([a._fixP, new Promise(function (r) { setTimeout(r, 2500); })]).then(function () {
        repairDoubleQuestion();
        window.location.href = a.getAttribute('href');
      });
      return;
    }
    var h = a.getAttribute('href');
    var first = h.indexOf('?');
    if (first !== -1 && h.indexOf('?', first + 1) !== -1) {
      var fixed = h.substring(0, first + 1) + h.substring(first + 1).replace(/\?/g, '&');
      a.setAttribute('href', fixed);
    }
  }, true);
})();